import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  ClientDTO,
  ComposableDashboardFiltersEnum,
  IComposableAnalyticsGlobalFilters,
  IUserRole,
  IWorkspace,
  axios,
  getAllClientList,
  getCurrentUserTeams,
  getWorkspaceUsers,
  selectedOrganizationalUnitManagers,
  selectedOrganizationalUnitTeams,
} from 'wisestep-api';
import {
  ScrollableTabs,
  WsColors,
  WsPageHeader,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { AccessControl, PinDashboardCollectionButton } from '../../../components';
import NoShadowBox from '../../../components/NoShadowBox';
import { IBreadCrumbsList, WsBreadCrumbs } from '../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../context';
import { useAnalyticsDashboardEditActionStore } from '../../../features/analyticsPage/store/useAnalyticsDashboardEditActionStore';
import getDecodedQueryParams from '../../../utils/getDecodedQueryParams';
import UserProfileOverview from '../../recruiterProfileDetails/components/userProfile/UserProfileOverview';
import TeamsList from '../../teams/components/TeamList';
import {
  getSettingBreadCrumbList,
  ISettingBreadCrumbTypes,
  Pages,
} from '../../teams/helper/defaultValues';
import AllManagersInWorkspaceList from '../../users/components/AllManagersInWorkspaceList';
import { getBreadCrumbWithLabel } from '../../users/helper/defaultValues';
import {
  gobackUrl,
  MethodType,
  ScopeType,
  tabstype,
  viewersApisCalls,
} from '../helper/ClientSettingsHelper';

import AllClientManagersInUnitList from './AllClientManagersInUnitList';
import AllClientsInUnitList from './AllClientsInUnitList';
import OrganizationalUnitDescriptionTab from './OrganizationalUnitDescriptionTab';
import ViwersList, { AccessType, getDefaultAllowedIds } from './ViewersList';

const OrganizationalUnitView = () => {
  const setViewAction = useAnalyticsDashboardEditActionStore((state) => state.setViewMode);
  const location = useLocation();
  const [param] = useSearchParams();
  const data = param.get('state') ?? '';
  const {
    organizationalUnitDescription: ouDescription,
    organizationalUnitId: ouId,
    organizationalUnitScopeType: ouScopeType,
    organizationalUnitName,
    organizationalUnitOwner,
    isTeamsOrganizationalUnit,
    isManagerOrganizationalUnit,
    isSettingsPage = false,
    defaultIndex = 0,
    isNewUnit = false,
    isClientsOrganizationalUnit,
    isClientContactOrganizationalUnit,
    clientOrClientContactIds,
  } = (location.state as any) ?? getDecodedQueryParams(data);

  const [tabIndex, setTabIndex] = useState<number>(defaultIndex);
  const [selectedManagers, setSelectedManagers] = useState<IWorkspace[]>([]);
  const [hasAccessToOrgUnit, setHasAccessToOrgUnit] = useState<boolean>(false);
  const [accountManagersIdsList, setAccountManagersIdsList] = useState<string[]>([]);
  const [teamsIdsList, setTeamsIdsList] = useState<string[]>([]);
  const [clientsList, setClientsList] = useState<ClientDTO[]>([]);
  const workspace = useWorkspaceContext();
  const hasViewAccessToOrgUnit =
    workspace.owns || workspace.userId === organizationalUnitOwner || hasAccessToOrgUnit;

  const tabs = [
    {
      tabName: 'Analytics',
    },
    {
      tabName: 'Description',
    },
    {
      tabName: tabstype[ouScopeType],
    },
    {
      tabName: 'Viewers',
    },
  ];

  const goBackUrl: IBreadCrumbsList[] = !isSettingsPage
    ? [{ label: 'DashBoards', url: gobackUrl[ouScopeType] }]
    : getSettingBreadCrumbList(
        ISettingBreadCrumbTypes.TEAMS_AND_USERS,
        Pages['ORGANIZATIONAL_UNITS']
      );

  const getSelectedAccountManagers = useCallback(async () => {
    const response = await selectedOrganizationalUnitManagers(ouId ?? '');
    const idsSelected: string[] = response.data.map(
      (item) => (item.type === 'Account Managers' || item.type === null) && item.accountManagerId
    );
    if (idsSelected && idsSelected.length > 0) {
      const query = {
        roles: [IUserRole.ACCOUNT_MANAGER],
        statuses: ['all'],
        userIds: idsSelected,
        page: 0,
        size: 400,
      };
      const req = await getWorkspaceUsers(query);
      const selectedData = req.data.workspaceUsers;
      setSelectedManagers(selectedData);
      const ids: string[] = selectedData?.map((item) => item.userId) ?? [];
      setAccountManagersIdsList(ids);
    } else {
      setSelectedManagers([]);
    }
  }, [ouId]);

  useEffect(() => {
    if (ouScopeType !== ScopeType.Account_Managers || !hasViewAccessToOrgUnit) return;
    getSelectedAccountManagers();
  }, [getSelectedAccountManagers, hasViewAccessToOrgUnit, ouScopeType]);

  useEffect(() => {
    if (workspace.owns || workspace.userId === organizationalUnitOwner) return;
    const checkAccessToUnit = async () => {
      const { data } = await axios.get(`/api/organizational-units/${ouId}/my-explicit-access`);
      if (data) {
        setHasAccessToOrgUnit(data.accessType === AccessType.GRANTED);
        return;
      }
      let hasAccess = false;
      const response = await viewersApisCalls(ouScopeType, MethodType.Get, ouId);
      const defaultAllowedIds = getDefaultAllowedIds(ouScopeType, response.data);
      if (ouScopeType == ScopeType.Teams) {
        const { data: user } = await getCurrentUserTeams();
        hasAccess =
          defaultAllowedIds.teamIds?.some(
            (teamId) => user.ownsTeams.includes(teamId) || user.belongsToTeams.includes(teamId)
          ) ?? false;
      }
      if (ouScopeType == ScopeType.Account_Managers) {
        hasAccess = defaultAllowedIds.userIds?.includes(workspace.userId ?? '') ?? false;
      }
      setHasAccessToOrgUnit(hasAccess);
    };
    checkAccessToUnit();
    // checkAccessToOrganizationalUnit(ouId ?? '').then((res) => setHasAccessToOrgUnit(res.data));
  }, [
    organizationalUnitOwner,
    ouId,
    ouScopeType,
    workspace.owns,
    workspace.role,
    workspace.userId,
  ]);

  useEffect(() => {
    if (ouScopeType !== ScopeType.Teams || !hasViewAccessToOrgUnit) return;
    selectedOrganizationalUnitTeams(ouId ?? '').then((response) => {
      const idsSelected: string[] = response.data.map((item) => item.teamId);
      setTeamsIdsList(idsSelected ?? []);
    });
  }, [hasViewAccessToOrgUnit, ouId, ouScopeType]);

  useEffect(() => {
    if (
      (ouScopeType !== ScopeType.Clients && ouScopeType !== ScopeType.Client_Contacts) ||
      !hasViewAccessToOrgUnit
    )
      return;
    const getClients = async () => {
      const response = await getAllClientList();
      setClientsList(response?.data?.data);
    };
    getClients();
  }, [hasViewAccessToOrgUnit, ouScopeType]);

  useEffect(() => {
    setViewAction(false);
  }, []);

  const dashboardFilters = useMemo(() => {
    const filters = {} as Record<ComposableDashboardFiltersEnum, IComposableAnalyticsGlobalFilters>;
    if (ouScopeType == ScopeType.Teams) {
      filters[ComposableDashboardFiltersEnum.TEAM] = { teamIds: teamsIdsList };
    } else {
      filters[ComposableDashboardFiltersEnum.ACCOUNT_MANAGER] = {
        accountManagers: accountManagersIdsList,
      };
    }
    return filters;
  }, [accountManagersIdsList, ouScopeType, teamsIdsList]);

  const renderOrgUnitEntityList = (scopeType: string) => {
    let headerText: string;
    let listingElement: JSX.Element | null;

    switch (scopeType) {
      case 'Teams':
        headerText = 'Delivery Unit Teams';
        listingElement = (
          <TeamsList
            readOnlyMode={true}
            isOrganizationalUnit={true}
            organizationalUnitId={ouId}
            isTeamsOrganizationalUnit={isTeamsOrganizationalUnit}
          />
        );
        break;
      case 'Account Managers':
        headerText = 'Account Managers in Unit';
        listingElement = (
          <AllManagersInWorkspaceList
            isOrganizationalUnit={true}
            addButtonText="Account Manager"
            organizationalUnitId={ouId}
            onFetch={getSelectedAccountManagers}
            getselectedManagers={selectedManagers}
            isManagerOrganizationalUnit={isManagerOrganizationalUnit}
          />
        );
        break;
      case 'Clients':
        headerText = 'Clients In Unit';
        listingElement = (
          <AllClientsInUnitList
            organizationalUnitId={ouId}
            allClientsList={clientsList}
            isClientUnit={isClientsOrganizationalUnit ?? false}
            organizationalUnitName={organizationalUnitName}
          />
        );
        break;
      case 'Client Contacts':
        headerText = 'Client Contacts In Unit';
        listingElement = (
          <AllClientManagersInUnitList
            organizationalUnitId={ouId}
            allClientList={clientsList}
            isClientManagerUnit={isClientContactOrganizationalUnit ?? false}
            organizationalUnitName={organizationalUnitName}
            isNewUnit={isNewUnit}
          />
        );
        break;
      default:
        headerText = '';
        listingElement = null;
    }

    return (
      <Box>
        <Box sx={{ mt: 2, mb: -2, ml: 1 }}>
          <WsTypography bold variant={WsTypoVariants.SUB_TITLE}>
            {headerText}
          </WsTypography>
        </Box>
        {listingElement}
      </Box>
    );
  };

  return (
    <>
      <Box mb={2}>
        <WsBreadCrumbs
          breadCrumbsList={[...goBackUrl, getBreadCrumbWithLabel(organizationalUnitName)]}
        />
        <WsPageHeader
          title={organizationalUnitName}
          rightChildren={
            <PinDashboardCollectionButton
              filters={dashboardFilters}
              entityId={ouId}
              entityName={organizationalUnitName}
            />
          }
        />
      </Box>
      {!hasViewAccessToOrgUnit ? (
        <AccessControl errorText="You don't have access to view the Unit's dashboard" />
      ) : (
        <Box bgcolor={WsColors.White} borderRadius={1.5} p={2}>
          <ScrollableTabs
            scrollList={tabs}
            getTabIndex={(index: number) => setTabIndex(index)}
            defaultValue={tabIndex}
          />
          {tabIndex === 0 && (
            <Box mt={-2.5}>
              <UserProfileOverview
                tabIndex={tabIndex}
                accountManagersOU={
                  ouScopeType === ScopeType.Account_Managers ? accountManagersIdsList : undefined
                }
                teamsOU={ouScopeType === ScopeType.Teams ? teamsIdsList : undefined}
                clientIds={ouScopeType === ScopeType.Clients ? clientOrClientContactIds : undefined}
                clientContactIds={
                  ouScopeType === ScopeType.Client_Contacts ? clientOrClientContactIds : undefined
                }
              />
            </Box>
          )}
          {tabIndex === 1 && (
            <NoShadowBox>
              <OrganizationalUnitDescriptionTab ouDescription={ouDescription} />
            </NoShadowBox>
          )}
          {tabIndex === 2 && renderOrgUnitEntityList(ouScopeType)}
          {tabIndex === 3 && (
            <>
              <Box sx={{ mt: 2, mb: -2, ml: 1.5 }}>
                <WsTypography bold variant={WsTypoVariants.SUB_TITLE}>
                  Viewers In Unit
                </WsTypography>
              </Box>
              <ViwersList
                organizationalUnitId={ouId}
                readonly={!isSettingsPage}
                organizationalUnitName={organizationalUnitName}
                scopeType={ouScopeType}
              />
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default OrganizationalUnitView;
